/**
 * useMenu Hook
 * React Query hook for menu-related data fetching
 */
import { useQuery } from '@tanstack/react-query';
import { menuService, MenuItem } from '@services/menu.service';

/**
 * Query keys for React Query cache management
 */
export const menuKeys = {
  all: ['menu'] as const,
  main: () => [...menuKeys.all, 'main'] as const,
  footer: () => [...menuKeys.all, 'footer'] as const,
};

/**
 * Hook to fetch main menu items
 */
export const useMainMenu = () => {
  return useQuery<MenuItem[]>({
    queryKey: menuKeys.main(),
    queryFn: () => menuService.getMainMenu(),
    staleTime: 10 * 60 * 1000, // 10 minutes (menu doesn't change often)
    gcTime: 30 * 60 * 1000, // 30 minutes (cache time)
    retry: 2,
  });
};

/**
 * Hook to fetch footer menu items (with nested children)
 */
export const useFooterMenu = () => {
  return useQuery<MenuItem[]>({
    queryKey: menuKeys.footer(),
    queryFn: () => menuService.getFooterMenu(),
    staleTime: 10 * 60 * 1000, // 10 minutes (menu doesn't change often)
    gcTime: 30 * 60 * 1000, // 30 minutes (cache time)
    retry: 2,
  });
};

export default useMainMenu;

